import { Box, Flex, Icon, Text } from '@chakra-ui/react';
import { RiCheckLine } from 'react-icons/ri';

import { useCampaignUI } from '../_ui/ui';

/**
 * The touch-sized controls every campaign screen is built from.
 *
 * ★ THE WHOLE SURFACE IS THE CONTROL. A radio's target is a 16px circle; the
 * card around it looked clickable and was not, which is the single most
 * common thing people got wrong on these screens. Everything below takes the
 * click anywhere on it, takes Enter and Space from the keyboard, and announces
 * itself to a screen reader as what it is — a radio or a checkbox — rather than
 * as a div that happens to change colour.
 *
 * ★ ONE HOME. The wizard, the list and the detail page all use these; a copy
 * per screen is how a fix lands in one place and not the other two.
 *
 * Every colour is a theme token; a hard-coded hex here would survive a preset
 * change and be the one thing on the page that did not.
 */

/** `PENDING_APPROVAL` → `Pending approval`. */
export const humanState = (s) =>
	String(s ?? '')
		.replace(/_/g, ' ')
		.toLowerCase()
		.replace(/^./, (m) => m.toUpperCase());

/**
 * One option in a set. Selected is a ring, not just a tint, so it reads in any preset.
 *
 * `multi` makes it a checkbox rather than a radio — same card, same click
 * target, but a screen reader is told that several may be on at once, which
 * is the truth for pinning sender numbers and a lie for choosing a source.
 */
export function ChoiceCard({ selected, onSelect, icon, title, description, disabled = false, multi = false, ...rest }) {
	const ui = useCampaignUI();
	const activate = () => {
		if (!disabled) onSelect?.();
	};
	return (
		<Box
			role={multi ? 'checkbox' : 'radio'}
			aria-checked={!!selected}
			aria-disabled={disabled || undefined}
			tabIndex={disabled ? -1 : 0}
			onClick={activate}
			onKeyDown={(e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					activate();
				}
			}}
			cursor={disabled ? 'not-allowed' : 'pointer'}
			position='relative'
			borderWidth='2px'
			borderColor={selected ? ui.accent : ui.border}
			bg={selected ? ui.accentSubtle : ui.surface}
			borderRadius='xl'
			p={4}
			opacity={disabled ? 0.55 : 1}
			transition='border-color 120ms ease, background 120ms ease, transform 120ms ease, box-shadow 120ms ease'
			_hover={disabled ? {} : { transform: 'translateY(-1px)', boxShadow: ui.shadowCard, borderColor: selected ? ui.accent : ui.borderStrong }}
			_focusVisible={{ outline: 'none', boxShadow: `0 0 0 3px ${ui.focusRing}` }}
			{...rest}
		>
			{selected && (
				<Flex
					position='absolute'
					top={3}
					right={3}
					boxSize='22px'
					borderRadius='full'
					bg={ui.accent}
					color={ui.textOnAccent}
					align='center'
					justify='center'
					aria-hidden
				>
					<Icon as={RiCheckLine} boxSize={3.5} />
				</Flex>
			)}
			<Flex gap={3} align='flex-start' pr={selected ? 8 : 0}>
				{icon && (
					<Flex
						align='center'
						justify='center'
						boxSize='40px'
						borderRadius='lg'
						bg={selected ? ui.accent : ui.surfaceSubtle}
						color={selected ? ui.textOnAccent : ui.accentInk}
						flexShrink={0}
					>
						<Icon as={icon} boxSize={5} />
					</Flex>
				)}
				<Box minW={0}>
					{/* Template names are snake_case with no break opportunities, so a
					    narrow card must be allowed to break inside the word rather
					    than overflow it. */}
					<Text fontSize='sm' fontWeight='semibold' color={ui.textPrimary} lineHeight='1.3' wordBreak='break-word'>
						{title}
					</Text>
					{description && (
						<Text fontSize='xs' color={ui.textMuted} mt={1} lineHeight='1.45'>
							{description}
						</Text>
					)}
				</Box>
			</Flex>
		</Box>
	);
}

/** The pill itself; `Pill*` below decide what role it plays. */
function PillBase({ on, tone, disabled, children, ...rest }) {
	const ui = useCampaignUI();
	const skip = tone === 'skip';
	return (
		<Box
			as='button'
			type='button'
			disabled={disabled}
			px={3.5}
			h='34px'
			minW='52px'
			borderRadius='full'
			borderWidth='1.5px'
			borderColor={on ? ui.accent : ui.border}
			bg={on ? ui.accentSubtle : ui.surface}
			color={on ? (skip ? ui.textMuted : ui.textPrimary) : ui.textSecondary}
			fontSize='sm'
			fontWeight={on ? 'semibold' : 'medium'}
			textDecoration={on && skip ? 'line-through' : 'none'}
			lineHeight='1'
			whiteSpace='nowrap'
			flexShrink={0}
			cursor={disabled ? 'not-allowed' : 'pointer'}
			transition='all 120ms ease'
			_hover={disabled ? {} : { borderColor: ui.accent, transform: 'translateY(-1px)' }}
			_active={{ transform: 'translateY(0)' }}
			_focusVisible={{ outline: 'none', boxShadow: `0 0 0 3px ${ui.focusRing}` }}
			{...rest}
		>
			{children}
		</Box>
	);
}

/**
 * A pill that is on or off. `tone='skip'` draws the on-state as a strike, for
 * a list where selecting means "leave this one out".
 */
export function ChipToggle({ checked, onToggle, children, tone = 'select', disabled = false, ...rest }) {
	return (
		<PillBase
			role='checkbox'
			aria-checked={!!checked}
			on={!!checked}
			tone={tone}
			disabled={disabled}
			onClick={() => {
				if (!disabled) onToggle?.();
			}}
			{...rest}
		>
			{children}
		</PillBase>
	);
}

/**
 * One of several, as a row of pills — the replacement for a `<select>` whose
 * options a person scans rather than searches.
 *
 * The row scrolls sideways on a phone instead of wrapping into a wall, so the
 * chosen one is always on the same line as its neighbours; the scrollbar is
 * hidden because the overflow is the affordance, not the bar.
 *
 * @param {Array<{value: string, label: string, count?: number}>} options
 */
export function PillSelect({ options, value, onChange, label, scroll = true, ...rest }) {
	const ui = useCampaignUI();
	return (
		<Flex
			role='radiogroup'
			aria-label={label}
			gap={2}
			wrap={scroll ? 'nowrap' : 'wrap'}
			overflowX={scroll ? 'auto' : 'visible'}
			pb={scroll ? 1 : 0}
			sx={scroll ? { scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } } : undefined}
			{...rest}
		>
			{options.map((o) => (
				<PillBase
					key={String(o.value)}
					role='radio'
					aria-checked={o.value === value}
					on={o.value === value}
					onClick={() => onChange?.(o.value)}
				>
					{o.label}
					{typeof o.count === 'number' && (
						<Text as='span' ml={1.5} fontSize='xs' color={o.value === value ? ui.accentInk : ui.textMuted}>
							{o.count.toLocaleString()}
						</Text>
					)}
				</PillBase>
			))}
		</Flex>
	);
}
